import { Component, OnInit, OnDestroy, HostBinding, NgZone, AfterViewInit } from "@angular/core";
import { Subject, merge, fromEvent } from "rxjs";
import { QueryParamsMap, UserInvitationService } from "src/app/core";
import { ScrollService, WindowService } from "@merri/common";
import { takeUntil } from "rxjs/operators";
import { LayoutService } from "src/app/layout";
import { Router, ResolveEnd, NavigationEnd } from "@angular/router";
import { Animations } from "src/app/shared";

@Component({
    selector: "app-floating-signup-button",
    templateUrl: "./floating-signup-button.component.html",
    styleUrls: ["./floating-signup-button.component.scss"],
    animations: [Animations.buildDefaultFadeInAnimation(550)],
    standalone: false
})
export class FloatingSignupButtonComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroyed$: Subject<void> = new Subject<void>();

  private mobileSize: number = 800;

  signupParams: QueryParamsMap;
  visible: boolean;
  canShow: boolean;

  @HostBinding("style.top") hostTop: string;
  @HostBinding("style.right") hostRight: string;

  constructor(
    private userInvitationService: UserInvitationService,
    private windowService: WindowService,
    private scrollService: ScrollService,
    private layoutService: LayoutService,
    private ngZone: NgZone,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.setSignupParams();

    this.userInvitationService.userInvitation$.pipe(takeUntil(this.destroyed$)).subscribe({ next: () => this.setSignupParams() });

    this.router.events.pipe(takeUntil(this.destroyed$)).subscribe({
      next: e => {
        if (e instanceof ResolveEnd) {
          this.reset(false);
        } else if (e instanceof NavigationEnd) {
          this.tryShow();
        }
      },
    });

    merge(this.layoutService.layoutChange$, this.scrollService.scroll$, fromEvent(window, "resize"))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.ngZone.run(() => {
            this.setPositionAndVisibility();
          });
        },
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewInit() {
    this.tryShow();
  }

  private tryShow(): void {
    setTimeout(() => {
      this.canShow = true;
      this.setPositionAndVisibility();
    }, 1000);
  }

  private setSignupParams() {
    this.signupParams = this.userInvitationService.getSignUpQueryParams();
  }

  private setPositionAndVisibility(): void {
    if (this.canShow) {
      const appHeader = document.getElementsByTagName("app-header")[0];
      const publicHeader = document.getElementsByTagName("app-public-header")[0];
      const homepageAnchor = document.getElementById("homepage-floating-sign-up-button-anchor");

      const appHeaderRect = appHeader && appHeader.getBoundingClientRect();
      const publicHeaderRect = publicHeader && publicHeader.getBoundingClientRect();
      const homepageAnchorRect = homepageAnchor && homepageAnchor.getBoundingClientRect();

      const hompeageAnchorVisible = homepageAnchorRect && homepageAnchorRect.top + homepageAnchorRect.height / 2 > 0;

      if (appHeaderRect && publicHeaderRect && !hompeageAnchorVisible) {
        const hostTopPx = Math.max(appHeaderRect.bottom + 20, 20);
        this.hostTop = `${hostTopPx}px`;

        const padding = this.windowService.matchesMax(this.mobileSize) ? 0 : 20;
        const hostRightPx = appHeaderRect.width - publicHeaderRect.right + padding;
        this.hostRight = `${hostRightPx}px`;

        this.visible = true;
        return;
      }
    }

    this.reset(this.canShow);
  }

  private reset(canShow: boolean): void {
    this.hostTop = "-1000px";
    this.hostRight = "-1000px";

    this.visible = false;
    this.canShow = canShow;
  }
}
